// Right-side slide-over detail panel — fully wired to the backend.

import { useEffect, useRef, useState } from "react";
import { addAnti, addDealLink, attachDocuments, deleteDealLink, deleteOutcome, downloadDocument, editDealFields, fetchOutcomes, getDealEmails, recordOutcome, type DealEmail } from "../api";
import { findMember, useAppData, useMe } from "../store";
import type { BreakdownRow, Deal, FieldCell, OutcomeEventRow, Status } from "../types";
import { Btn, IconBtn, Pill, Avatar, TextInput } from "../components/Primitives";
import { Ic, Mark } from "../components/Icon";
import { Logo } from "../components/Logo";
import { Md } from "../components/Md";
import { MultMini } from "../components/Multiplier";
import { OwnerPopover } from "../components/OwnerPopover";
import { RichTextEditor } from "../components/RichTextEditor";
import type { DecideKind } from "./DecideModal";
import { PriorWork } from "./PriorWork";

function fmtTime(raw: string | undefined): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    + " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

type Round = NonNullable<Deal["rounds"]>[number];

const normStage = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();

/** Mirror of the backend's size canonicalization: "$2.5M" == "2,500,000" == 2500000. */
function normSize(v: number | string | null | undefined): number | string {
  if (v == null || v === "" || v === "—") return "";
  if (typeof v === "number") return v;
  const s = v.trim().toLowerCase().replace(/[,$~\s]/g, "");
  const mult = s.endsWith("m") ? 1e6 : s.endsWith("k") ? 1e3 : s.endsWith("b") ? 1e9 : 1;
  const m = /-?\d+(\.\d+)?/.exec(s);
  return m ? Number(m[0]) * mult : s;
}

/** Collapse consecutive identical (stage, round size) arrivals into one row —
 *  also covers history written before re-submissions were deduped server-side. */
function groupRounds(rounds: Round[]): (Round & { seen: number })[] {
  const groups: (Round & { seen: number })[] = [];
  for (const r of rounds) {
    const prev = groups[groups.length - 1];
    if (prev && normStage(prev.stage) === normStage(r.stage)
        && normSize(prev.roundSize) === normSize(r.roundSize)) {
      prev.seen += r.seen ?? 1;
      prev.date = r.lastDate ?? r.date ?? prev.date;
      if (r.score != null) prev.score = r.score;
    } else {
      groups.push({ ...r, n: groups.length + 1, seen: r.seen ?? 1, date: r.lastDate ?? r.date });
    }
  }
  return groups;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FIELD_ORDER = [
  "Stage", "Round size", "Sector", "Geography",
  "Founders", "Prior exit", "Team size", "Valuation (cap)",
];

function ConfDot({ level }: { level: string }) {
  const w = level === "High" ? 3 : level === "Medium" ? 2 : 1;
  return (
    <span style={{ display: "inline-flex", gap: 1.5, alignItems: "center", marginLeft: 3 }}>
      {[1, 2, 3].map((i) => (
        <i key={i} style={{
          display: "inline-block", width: 3, height: 3, borderRadius: 2,
          background: i <= w ? "var(--ink)" : "var(--border-strong)",
        }} />
      ))}
    </span>
  );
}

interface FieldPopoverProps {
  label: string;
  info?: FieldCell;
  anchor: DOMRect;
  onClose: () => void;
}

function FieldPopover({ label, info, anchor, onClose }: FieldPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  const w = 320;
  const top = anchor.bottom + 6;
  let left = anchor.left;
  if (left + w + 12 > window.innerWidth) left = window.innerWidth - w - 12;

  const valueText = info && info.value != null && info.value !== ""
    ? (Array.isArray(info.value) ? info.value.join(", ")
      : typeof info.value === "number" ? info.value.toLocaleString()
      : /^\d{4,}$/.test(String(info.value)) ? Number(info.value).toLocaleString()
      : String(info.value))
    : "Not extracted";

  return (
    <div ref={ref} className="pop" style={{ top, left, position: "fixed" }}>
      <div className="h">{label}</div>
      <div className={"v" + (!info || valueText === "Not extracted" ? " unknown" : "")}>
        {valueText}
      </div>
      {info?.note && <p>{info.note}</p>}
      {info?.quote && <p className="quote">&ldquo;{info.quote}&rdquo;</p>}
      <div className="src">
        <Ic name={info?.source?.includes("Email") ? "mail" : "info"} size={12} />
        <span>From {info?.source || "—"}</span>
        <span className="conf">{info?.conf || "—"} <ConfDot level={info?.conf || ""} /></span>
      </div>
    </div>
  );
}

// ── Outcome loop (KG-7): what happened to this company after we saw it ──

const EVENT_LABEL: Record<OutcomeEventRow["event"], string> = {
  acquisition: "Acquired",
  ipo: "IPO",
  shutdown: "Shut down",
  new_round: "Raised again",
};

function fmtUsd(v: number | null): string {
  if (v == null) return "";
  if (v >= 1e9) return `$${+(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${+(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${+(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
}

function outcomeSummary(o: OutcomeEventRow): string {
  const value = fmtUsd(o.valueUsd);
  const when = o.date ? ` (${o.date})` : "";
  if (o.event === "acquisition") {
    return `Acquired${o.acquirer ? ` by ${o.acquirer}` : ""}${value ? ` for ${value}` : ""}${when}`;
  }
  if (o.event === "ipo") return `IPO${value ? ` at ${value}` : ""}${when}`;
  if (o.event === "shutdown") return `Shut down${when}`;
  return `Raised again${value ? ` — ${value}` : ""}${when}`;
}

function OutcomeSection({ deal, outcomes, onChange, refresh }: {
  deal: Deal;
  outcomes: OutcomeEventRow[];
  onChange: (rows: OutcomeEventRow[]) => void;
  refresh: () => Promise<void>;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [event, setEvent] = useState<OutcomeEventRow["event"]>("new_round");
  const [date, setDate] = useState("");
  const [value, setValue] = useState("");
  const [acquirer, setAcquirer] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [antiAdded, setAntiAdded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const row = await recordOutcome({
        dealSlug: deal.id, event, date: date.trim(),
        valueUsd: event === "shutdown" ? null : value.trim() || null,
        acquirer: event === "acquisition" ? acquirer.trim() : "",
        note: note.trim(),
      });
      onChange([row, ...outcomes]);
      setFormOpen(false);
      setDate(""); setValue(""); setAcquirer(""); setNote("");
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteOutcome(id);
      onChange(outcomes.filter((o) => o.id !== id));
    } catch { /* row stays; nothing to explain inline */ }
  };

  const toAnti = async (o: OutcomeEventRow) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await addAnti({ dealId: deal.id, then: outcomeSummary(o) });
      await refresh();
      setAntiAdded(o.id);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  if (outcomes.length === 0 && !["passed", "rejected", "invested"].includes(deal.status) && !formOpen) {
    return null;  // live pipeline deals rarely have an afterlife yet
  }

  const input = (v: string, set: (s: string) => void, placeholder: string, width = 110) => (
    <input
      className="input" value={v} placeholder={placeholder}
      onChange={(e) => set(e.target.value)}
      style={{ width, fontSize: 12 }}
    />
  );

  return (
    <>
      <div style={{ padding: "10px 22px 6px" }}>
        <div className="h-section">
          <h3>What happened next</h3>
          <span className="eyebrow" style={{ color: "var(--ink-3)", textTransform: "none", letterSpacing: 0 }}>
            · outcomes after the decision
          </span>
          <span style={{ flex: 1 }} />
          {!formOpen && (
            <Btn size="sm" ico="plus" onClick={() => setFormOpen(true)}>Record outcome</Btn>
          )}
        </div>
      </div>
      <div style={{ padding: "0 22px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
        {outcomes.map((o) => (
          <div key={o.id} style={{
            display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
            background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 7,
          }}>
            <span style={{
              fontSize: 10.5, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.05em", padding: "1px 7px", borderRadius: 8,
              color: o.event === "shutdown" ? "var(--ink-3)" : "var(--warn)",
              background: o.event === "shutdown" ? "var(--raised)" : "var(--warn-soft)",
            }}>
              {EVENT_LABEL[o.event]}
            </span>
            <span style={{ fontSize: 12.5, color: "var(--ink-2)", flex: 1, minWidth: 0 }}>
              {outcomeSummary(o)}
              {o.note && o.note !== outcomeSummary(o) && (
                <span style={{ color: "var(--ink-3)" }}> — {o.note}</span>
              )}
            </span>
            {o.antiEligible && (
              antiAdded === o.id ? (
                <span style={{ fontSize: 11, color: "var(--pos)", fontWeight: 600, whiteSpace: "nowrap" }}>
                  In anti-portfolio
                </span>
              ) : (
                <Btn size="sm" disabled={busy} onClick={() => toAnti(o)}>
                  Add to anti-portfolio
                </Btn>
              )
            )}
            <button
              title="Delete outcome"
              onClick={() => remove(o.id)}
              style={{ border: "none", background: "none", cursor: "pointer", color: "var(--ink-4)", padding: 2, lineHeight: 0 }}
            >
              <Ic name="x" size={11} />
            </button>
          </div>
        ))}
        {error && !formOpen && (
          <div style={{ fontSize: 11.5, color: "var(--neg)" }}>{error}</div>
        )}
        {formOpen && (
          <div style={{
            display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            padding: "8px 12px", background: "var(--bg)",
            border: "1px solid var(--border)", borderRadius: 7,
          }}>
            <select
              className="input" value={event}
              onChange={(e) => setEvent(e.target.value as OutcomeEventRow["event"])}
              style={{ fontSize: 12, width: 130 }}
            >
              <option value="new_round">Raised again</option>
              <option value="acquisition">Acquired</option>
              <option value="ipo">IPO</option>
              <option value="shutdown">Shut down</option>
            </select>
            {event === "acquisition" && input(acquirer, setAcquirer, "Acquirer", 120)}
            {event !== "shutdown" && input(value, setValue, "$40M", 80)}
            {input(date, setDate, "2026", 70)}
            {input(note, setNote, "Note (optional)", 160)}
            <Btn size="sm" kind="accent" disabled={busy} onClick={submit}>
              {busy ? "Saving…" : "Save"}
            </Btn>
            <Btn size="sm" kind="ghost" onClick={() => { setFormOpen(false); setError(null); }}>Cancel</Btn>
            {error && <span style={{ fontSize: 11, color: "var(--neg)" }}>{error}</span>}
          </div>
        )}
      </div>
    </>
  );
}

interface Props {
  id: string;
  onClose: () => void;
  onDecide: (kind: DecideKind) => void;
  onOpenDeal?: (id: string) => void;
  onOpenPerson?: (entityId: string) => void;
}

export function DetailPanel({ id, onClose, onDecide, onOpenDeal, onOpenPerson }: Props) {
  const { data, patchDeal, addNote, deleteDeal, refresh, upsertDeal } = useAppData();
  const me = useMe();
  const deal: Deal | undefined =
    data?.deals.find((x) => x.id === id) ?? data?.archive.find((x) => x.id === id);

  const [people, setPeople] = useState<{ id: string; name: string; role: string }[]>([]);
  useEffect(() => setPeople([]), [id]);   // no stale chips when switching deals
  const [emails, setEmails] = useState<DealEmail[]>([]);
  const [openEmail, setOpenEmail] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setEmails([]); setOpenEmail(null);
    getDealEmails(id)
      .then((rows) => { if (live) setEmails(rows); })
      .catch(() => { /* correspondence is additive — never block the panel */ });
    return () => { live = false; };
  }, [id]);
  const [outcomes, setOutcomes] = useState<OutcomeEventRow[]>([]);
  useEffect(() => {
    let live = true;
    setOutcomes([]);
    fetchOutcomes(id)
      .then((rows) => { if (live) setOutcomes(rows); })
      .catch(() => { /* outcomes are additive — never block the panel */ });
    return () => { live = false; };
  }, [id]);
  const [pop, setPop] = useState<{ label: string; info?: FieldCell; anchor: DOMRect } | null>(null);
  const [ownerPop, setOwnerPop] = useState<DOMRect | null>(null);
  const [statusPop, setStatusPop] = useState<DOMRect | null>(null);
  const [antiBusy, setAntiBusy] = useState(false);
  const [attachBusy, setAttachBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkDeleting, setLinkDeleting] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const removeLink = async (linkId: string) => {
    if (!deal || linkDeleting) return;
    setLinkDeleting(linkId); setLinkError(null);
    try {
      upsertDeal(await deleteDealLink(deal.id, linkId));
    } catch (e) {
      setLinkError(String((e as Error).message || e));
    } finally {
      setLinkDeleting(null);
    }
  };
  const fileInput = useRef<HTMLInputElement>(null);
  const [editFields, setEditFields] = useState(false);
  const [draft, setDraft] = useState<Record<string, FieldCell>>({});
  const [fieldsBusy, setFieldsBusy] = useState(false);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (pop) setPop(null);
        else if (ownerPop) setOwnerPop(null);
        else if (statusPop) setStatusPop(null);
        else onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, pop, ownerPop, statusPop]);

  if (!deal) return null;

  const openField = (label: string, e: React.MouseEvent) =>
    setPop({ label, info: deal.fields?.[label], anchor: (e.currentTarget as HTMLElement).getBoundingClientRect() });

  const total = (deal.breakdown ?? []).reduce(
    (s: number, r: BreakdownRow) => s + (r.passed ? Math.round(r.mult * 10) : 0),
    0,
  );

  const fieldList = FIELD_ORDER.filter((l) => deal.fields && l in deal.fields);

  const startEditFields = () => {
    setDraft(JSON.parse(JSON.stringify(deal.fields ?? {})));
    setEditFields(true);
  };
  const setFieldValue = (label: string, raw: string) => {
    setDraft((d) => {
      const cell = d[label] ?? { value: null, source: "Edited", conf: "High", note: "", quote: "" };
      const orig = deal.fields?.[label]?.value;
      let val: unknown = raw;
      if (Array.isArray(orig)) val = raw.split(",").map((s) => s.trim()).filter(Boolean);
      else if (typeof orig === "number") val = raw === "" ? null : Number(raw);
      else val = raw === "" ? null : raw;
      return { ...d, [label]: { ...cell, value: val as FieldCell["value"], source: "Edited" } };
    });
  };
  const saveFields = async () => {
    setFieldsBusy(true);
    try {
      upsertDeal(await editDealFields(deal.id, { fields: draft }));
      setEditFields(false);
    } finally {
      setFieldsBusy(false);
    }
  };
  const updateTags = async (tags: string[]) => {
    upsertDeal(await editDealFields(deal.id, { tags }));
  };
  const cellText = (v: FieldCell["value"]): string =>
    v == null ? "" : Array.isArray(v) ? v.join(", ") : String(v);

  const onPickOwner = async (ownerId: string | null) => {
    setOwnerPop(null);
    await patchDeal(deal.id, { owner: ownerId });
  };

  const onPickStatus = async (status: Status) => {
    setStatusPop(null);
    await patchDeal(deal.id, { status });
  };

  const onSubmitNote = async (html: string) => {
    const text = html.replace(/<[^>]+>/g, " ").trim();
    if (!text) return;
    await addNote(deal.id, html);
  };

  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className="slideover" role="dialog">
        <div className="detail-header">
          <div className="detail-title">
            <Logo domain={deal.domain} co={deal.co} size={40} radius={8} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="co">{deal.co}</div>
              {people.length > 0 && onOpenPerson ? (
                <div className="founders" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {people.map((p) => (
                    <span
                      key={p.id}
                      onClick={() => onOpenPerson(p.id)}
                      title={`Everything the firm knows about ${p.name}`}
                      style={{
                        cursor: "pointer", padding: "1px 8px", borderRadius: 10,
                        background: "var(--raised)", border: "1px solid var(--border)",
                        color: "var(--ink-2)",
                      }}
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="founders">{deal.founders}</div>
              )}
              {deal.brief && (
                <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 4, lineHeight: 1.5, maxWidth: 520 }}>
                  <Md text={deal.brief} />
                </div>
              )}
            </div>
            <div className="score-block">
              <span className="num">{deal.score ?? "—"}</span>
              {deal.score != null && (
                <span className="score-bar">
                  <i style={{ width: deal.score + "%" }} />
                </span>
              )}
            </div>
            {deal.source === "upload" && (
              <IconBtn
                ico="trash"
                label="Delete deal"
                onClick={async () => { await deleteDeal(deal.id); onClose(); }}
              />
            )}
            <IconBtn ico="x" onClick={onClose} label="Close" />
          </div>
          <div className="detail-meta">
            <span
              style={{ cursor: "pointer" }}
              onClick={(e) => setStatusPop(e.currentTarget.getBoundingClientRect())}
            >
              <Pill kind={deal.status} />
            </span>
            <span className="sep">·</span>
            <Avatar
              id={deal.owner} size="sm"
              onClick={(e) => setOwnerPop((e.currentTarget as HTMLElement).getBoundingClientRect())}
            />
            <span style={{ cursor: "pointer" }}
                  onClick={(e) => setOwnerPop((e.currentTarget as HTMLElement).getBoundingClientRect())}>
              {deal.owner
                ? findMember(data?.config.team, deal.owner)?.name ?? "Unassigned"
                : "Unassigned"}
            </span>
            <span className="sep">·</span>
            <span>Route: <strong style={{ fontWeight: 500, color: "var(--ink)" }}>{deal.route}</strong></span>
            <span className="sep">·</span>
            <span>{fmtTime(deal.timeFull)}</span>
            {deal.referredBy?.name && (
              <>
                <span className="sep">·</span>
                <span
                  title={deal.referredBy.email}
                  style={{
                    fontSize: 10.5, fontWeight: 600, color: "var(--accent)",
                    background: "var(--accent-soft)", padding: "1px 8px",
                    borderRadius: 8, whiteSpace: "nowrap",
                  }}
                >
                  Referred by {deal.referredBy.name}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="detail-actions">
          <Btn ico="check" size="sm" onClick={() => onDecide("review")}>Mark reviewed</Btn>
          <Btn ico="x" size="sm" onClick={() => onDecide("pass")}>Pass</Btn>
          <Btn ico="check" kind="accent" size="sm" onClick={() => onDecide("invest")}>Invest</Btn>
          <span style={{ flex: 1 }} />
          {deal.status === "passed" && (
            <Btn
              size="sm" disabled={antiBusy}
              onClick={async () => {
                setAntiBusy(true);
                try { await addAnti({ dealId: deal.id }); await refresh(); }
                finally { setAntiBusy(false); }
              }}
            >{antiBusy ? "Adding…" : "Add to anti-portfolio"}</Btn>
          )}
        </div>

        <div className="detail-body">
          {/* Extracted fields */}
          <div style={{ padding: "16px 22px 6px" }}>
            <div className="h-section">
              <h3>Extracted fields</h3>
              <span className="eyebrow" style={{ color: "var(--ink-3)", textTransform: "none", letterSpacing: 0 }}>
                · {editFields ? "correct any value — saving re-scores the deal" : "tap any to see how Prism decided"}
              </span>
              <span style={{ flex: 1 }} />
              {editFields ? (
                <>
                  <Btn size="sm" kind="ghost" onClick={() => setEditFields(false)}>Cancel</Btn>
                  <Btn size="sm" kind="primary" disabled={fieldsBusy} onClick={saveFields}>
                    {fieldsBusy ? "Saving…" : "Save"}
                  </Btn>
                </>
              ) : (
                <Btn size="sm" ico="sliders" onClick={startEditFields}>Edit</Btn>
              )}
            </div>
          </div>
          <div className="field-grid">
            {fieldList.map((label) => {
              const info = deal.fields?.[label];
              const value = info?.value;
              const display = value == null || value === ""
                ? "Not extracted"
                : Array.isArray(value) ? value.join(", ")
                : typeof value === "number" ? value.toLocaleString()
                : /^\d{4,}$/.test(String(value)) ? Number(value).toLocaleString()
                : String(value);
              if (editFields) {
                return (
                  <div key={label} className="field">
                    <div className="label">{label}</div>
                    <input
                      className="input"
                      value={cellText(draft[label]?.value)}
                      placeholder="—"
                      onChange={(e) => setFieldValue(label, e.target.value)}
                      style={{ width: "100%", padding: "4px 8px", fontSize: 13, marginTop: 2 }}
                    />
                  </div>
                );
              }
              return (
                <div key={label} className="field" onClick={(e) => openField(label, e)}>
                  <div className="label">
                    {label}
                    <span className="src">{info?.source ?? "—"}</span>
                  </div>
                  <div className={"value" + (display === "Not extracted" ? " unknown" : "")}>
                    {display}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tags */}
          <div style={{ padding: "14px 22px 6px" }}>
            <div className="h-section"><h3>Tags</h3></div>
          </div>
          <div style={{ padding: "0 22px 6px", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            {(deal.tags ?? []).map((t) => (
              <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", background: "var(--raised)", border: "1px solid var(--border)", borderRadius: 999, fontSize: 12 }}>
                {t}
                <span onClick={() => void updateTags((deal.tags ?? []).filter((x) => x !== t))}
                  style={{ cursor: "pointer", color: "var(--ink-4)" }}><Ic name="x" size={9} /></span>
              </span>
            ))}
            <input
              className="input" value={tagInput} placeholder="add tag…"
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                const v = tagInput.trim();
                if (e.key === "Enter" && v && !(deal.tags ?? []).includes(v)) {
                  void updateTags([...(deal.tags ?? []), v]);
                  setTagInput("");
                }
              }}
              style={{ width: 110, padding: "3px 9px", fontSize: 12, borderRadius: 999 }}
            />
          </div>

          {/* Inbound history — grouped by round; identical arrivals collapse */}
          {deal.rounds && deal.rounds.reduce((s, r) => s + (r.seen ?? 1), 0) > 1 && (
            <>
              <div style={{ padding: "14px 22px 6px" }}>
                <div className="h-section">
                  <h3>Inbound</h3>
                  <span className="eyebrow" style={{ color: "var(--ink-3)", textTransform: "none", letterSpacing: 0 }}>
                    · this company has come in {deal.rounds.reduce((s, r) => s + (r.seen ?? 1), 0)} times
                  </span>
                </div>
              </div>
              <div style={{ padding: "0 22px 6px", display: "flex", flexDirection: "column", gap: 6 }}>
                {groupRounds(deal.rounds).map((r) => (
                  <div key={r.n} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 7, fontSize: 12.5 }}>
                    <span style={{ fontFamily: "var(--mono)", color: "var(--ink-3)" }}>#{r.n}</span>
                    <span style={{ fontWeight: 500 }}>{r.stage ?? "—"}</span>
                    {r.roundSize != null && r.roundSize !== "" && (
                      <span style={{ color: "var(--ink-2)" }}>{typeof r.roundSize === "number" ? `$${Number(r.roundSize).toLocaleString()}` : r.roundSize}</span>
                    )}
                    {r.seen > 1 && (
                      <span style={{ color: "var(--ink-3)" }}>· seen {r.seen}×</span>
                    )}
                    <span style={{ flex: 1 }} />
                    {r.score != null && <span className="score" style={{ fontSize: 13 }}>{r.score}</span>}
                    {r.date && <span style={{ color: "var(--ink-3)" }}>{r.seen > 1 ? "last " : ""}{fmtTime(r.date)}</span>}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Score breakdown */}
          <div style={{ padding: "10px 22px 6px" }}>
            <div className="h-section">
              <h3>Score breakdown</h3>
              <span className="eyebrow" style={{ color: "var(--ink-3)", textTransform: "none", letterSpacing: 0 }}>
                · every rule, its weight, the value that triggered it
              </span>
            </div>
          </div>
          {deal.breakdown && deal.breakdown.length > 0 ? (
            <div className="breakdown">
              {deal.breakdown.map((r, i) => {
                const contribution = r.passed ? Math.round(r.mult * 10) : 0;
                return (
                  <div key={i} className="row-r"
                    style={{ gridTemplateColumns: "1fr 150px 50px", gap: 14, opacity: r.passed ? 1 : 0.55 }}>
                    <div className="lbl">
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{
                          width: 14, height: 14, borderRadius: 7,
                          display: "inline-grid", placeItems: "center",
                          background: r.passed ? "var(--ink)" : "transparent",
                          color: "var(--bg)",
                          border: r.passed ? "0" : "1px dashed var(--ink-4)",
                        }}>
                          {r.passed ? <Ic name="check" size={9} sw={3} /> : null}
                        </span>
                        <span>{r.l}</span>
                      </div>
                      <small style={{ marginLeft: 22 }}>{r.d}</small>
                    </div>
                    <MultMini value={r.mult} failed={!r.passed} />
                    <span className="w" style={{ color: r.passed ? "var(--ink)" : "var(--ink-4)" }}>
                      {r.passed ? "+" + contribution : "—"}
                    </span>
                  </div>
                );
              })}
              <div className="total">
                <span className="l">
                  Total · {deal.breakdown.filter((r) => r.passed).length} of {deal.breakdown.length} rules passed
                </span>
                <span className="n">{total}</span>
              </div>
            </div>
          ) : (
            <div style={{ padding: "0 22px 18px", color: "var(--ink-3)", fontSize: 12 }}>
              No rules ran on this deal.
            </div>
          )}

          {/* Prior work — precedents · same-sector history · firm view */}
          <PriorWork dealId={deal.id} onOpenDeal={onOpenDeal} onPeople={setPeople} />

          {/* Outcome loop — what happened after the decision. Keyed by deal
              so a half-typed draft can never land on the next deal opened. */}
          <OutcomeSection
            key={deal.id}
            deal={deal} outcomes={outcomes} onChange={setOutcomes} refresh={refresh}
          />

          {/* Enrichment */}
          {deal.enrichment && Object.keys(deal.enrichment).length > 0 && (
            <>
              <div style={{ padding: "10px 22px 6px" }}>
                <div className="h-section"><h3>Enrichment</h3></div>
              </div>
              <div className="enrich-grid">
                {Object.entries(deal.enrichment).map(([name, e]) => {
                  const dot = e.state === "error" ? "var(--neg)"
                    : e.state === "inferred" ? "var(--ink-4)" : "var(--pos)";
                  const label = e.state === "inferred" ? "AI-inferred"
                    : e.state === "fetched" ? "Live" : null;
                  return (
                    <div key={name} className={"enrich-card" + (e.state === "loading" ? " loading" : "")}>
                      <div className="h">
                        {e.state === "loading"
                          ? <Ic name="spinner" size={12} />
                          : <span style={{ width: 6, height: 6, borderRadius: 3, background: dot }} />}
                        <span>{name}</span>
                        {label && (
                          <span style={{
                            marginLeft: 6, fontSize: 9.5, fontWeight: 500,
                            textTransform: "uppercase", letterSpacing: "0.07em",
                            color: "var(--ink-3)", background: "var(--raised)",
                            padding: "1px 6px", borderRadius: 999,
                          }}>{label}</span>
                        )}
                        <span style={{ marginLeft: "auto", color: "var(--ink-3)" }}>
                          <Ic name="chevDown" size={12} />
                        </span>
                      </div>
                      <div className="summary"><Md text={e.summary} /></div>
                      {e.data?.sources && e.data.sources.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 6 }}>
                          {e.data.sources.slice(0, 4).map((u) => {
                            let host = u;
                            try { host = new URL(u).hostname.replace(/^www\./, ""); } catch { /* keep raw */ }
                            return (
                              <a key={u} href={u} target="_blank" rel="noopener noreferrer"
                                onClick={(ev) => ev.stopPropagation()}
                                style={{ fontSize: 11, color: "var(--accent)", textDecoration: "none", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                <Ic name="ext" size={9} /> {host}
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Documents */}
          <div style={{ padding: "10px 22px 6px" }}>
            <div className="h-section">
              <h3>Documents</h3>
              <span style={{ flex: 1 }} />
              <input
                ref={fileInput} type="file" multiple style={{ display: "none" }}
                onChange={async (e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (!files.length) return;
                  setAttachBusy(true);
                  try {
                    upsertDeal(await attachDocuments(deal.id, files));
                  } finally {
                    setAttachBusy(false);
                  }
                }}
              />
              <Btn size="sm" kind="ghost" ico="ext" onClick={() => { setLinkOpen(!linkOpen); setLinkError(null); }}>
                Add link
              </Btn>
              <Btn size="sm" ico="plus" disabled={attachBusy}
                   onClick={() => fileInput.current?.click()}>
                {attachBusy ? "Attaching…" : "Attach"}
              </Btn>
            </div>
            <div style={{ color: "var(--ink-3)", fontSize: 11.5, marginTop: 2 }}>
              Memos and decks attached here become queryable in Knowledge — so do
              linked Google Docs shared with "anyone with the link".
            </div>
            {linkOpen && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <TextInput value={linkUrl} onChange={setLinkUrl}
                    placeholder="https://docs.google.com/… or any URL" style={{ flex: 2 }} />
                  <TextInput value={linkTitle} onChange={setLinkTitle}
                    placeholder="Title (optional)" style={{ flex: 1 }} />
                  <Btn size="sm" kind="primary" disabled={linkBusy || !linkUrl.trim()}
                    onClick={async () => {
                      setLinkBusy(true); setLinkError(null);
                      try {
                        upsertDeal(await addDealLink(deal.id, linkUrl.trim(), linkTitle.trim() || undefined));
                        setLinkUrl(""); setLinkTitle(""); setLinkOpen(false);
                      } catch (e) {
                        setLinkError(String((e as Error).message || e));
                      } finally {
                        setLinkBusy(false);
                      }
                    }}>
                    {linkBusy ? "Adding…" : "Add"}
                  </Btn>
                </div>
                {linkError && (
                  <div style={{ color: "var(--neg)", fontSize: 11.5, marginTop: 4 }}>{linkError}</div>
                )}
              </div>
            )}
            {(deal.links ?? []).length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                {(deal.links ?? []).map((l) => (
                  <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 11px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 7, fontSize: 12.5 }}>
                    <Ic name="ext" size={12} />
                    <a href={l.url} target="_blank" rel="noopener noreferrer"
                       style={{ color: "var(--ink)", fontWeight: 500, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: "0 1 auto" }}>
                      {l.title}
                    </a>
                    <span style={{ color: "var(--ink-3)", fontSize: 11.5, whiteSpace: "nowrap", flexShrink: 0 }}>{l.host}</span>
                    {l.indexed && (
                      <span style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--pos, #2e7d32)", background: "var(--pos-soft, rgba(46,125,50,0.08))", padding: "1px 7px", borderRadius: 999, whiteSpace: "nowrap", flexShrink: 0 }}>
                        in Knowledge
                      </span>
                    )}
                    <span style={{ flex: 1 }} />
                    <span onClick={() => void removeLink(l.id)}
                      style={{ cursor: "pointer", color: "var(--ink-4)", opacity: linkDeleting === l.id ? 0.4 : 1, flexShrink: 0 }}>
                      <Ic name="x" size={11} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {deal.documents && deal.documents.length > 0 && (
            <>
              <div className="docs-grid">
                {deal.documents.map((doc) => {
                  const ext = doc.name.split(".").pop()?.toUpperCase() || "FILE";
                  return (
                    <a
                      key={doc.name}
                      className="doc-card"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        void downloadDocument(deal.id, doc.name).catch(() => {});
                      }}
                    >
                      <span className="doc-icon">
                        <span className="doc-ext">{ext}</span>
                      </span>
                      <span className="doc-info">
                        <span className="doc-name">{doc.name}</span>
                        <span className="doc-meta">{formatSize(doc.size)}</span>
                      </span>
                      <span className="doc-dl">
                        <Ic name="ext" size={13} />
                      </span>
                    </a>
                  );
                })}
              </div>
            </>
          )}

          {/* Correspondence — durable per-message timeline; falls back to
              the legacy single emailBody for deals from before the table. */}
          {emails.length > 0 ? (
            <>
              <div style={{ padding: "10px 22px 6px" }}>
                <div className="h-section">
                  <h3>Correspondence</h3>
                  <span className="eyebrow" style={{ color: "var(--ink-3)", textTransform: "none", letterSpacing: 0 }}>
                    · {emails.length} message{emails.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div style={{ padding: "0 22px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                {emails.map((m) => (
                  <div key={m.id} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 7 }}>
                    <div
                      onClick={() => setOpenEmail(openEmail === m.id ? null : m.id)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", cursor: "pointer" }}
                    >
                      <Ic name={m.direction === "out" ? "send" : "mail"} size={12} />
                      <span style={{ fontSize: 12.5, fontWeight: 500, flex: 1, minWidth: 0,
                                     overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {m.subject || "(no subject)"}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{fmtTime(m.sentAt)}</span>
                    </div>
                    {openEmail === m.id && (
                      <div style={{ padding: "0 10px 10px", fontSize: 12, color: "var(--ink-2)", whiteSpace: "pre-wrap" }}>
                        <div style={{ fontSize: 11, color: "var(--ink-3)", marginBottom: 4 }}>From {m.sender}</div>
                        {m.body}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : deal.emailBody && (
            <>
              <div style={{ padding: "10px 22px 6px" }}>
                <div className="h-section"><h3>Original email</h3></div>
              </div>
              <div className="email-block">
                <div className="from">From {deal.email || deal.founders} · {fmtTime(deal.timeFull)}</div>
                <div className="subject">{deal.co} — fundraising</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{deal.emailBody}</div>
              </div>
            </>
          )}

          {/* Notes */}
          <div style={{ padding: "10px 22px 6px" }}>
            <div className="h-section"><h3>Notes</h3></div>
          </div>
          <div className="notes-block">
            {(deal.notes ?? []).map((n) => {
              // System notes (round detection, etc.) have no author — attribute
              // them to Prism rather than a (non-existent) "Former member".
              const system = !n.author;
              return (
                <div key={n.id} className="note">
                  {system ? (
                    <span className="av sm" title="Prism" style={{
                      display: "inline-grid", placeItems: "center",
                      background: "var(--ink)", color: "var(--bg)", border: "none",
                    }}>
                      <Mark size={12} />
                    </span>
                  ) : (
                    <Avatar id={n.author} size="sm" />
                  )}
                  <div className="body">
                    <div className="who">
                      {system
                        ? "Prism"
                        : (findMember(data?.config.team, n.author)?.name ?? "Former member")}
                      {" · "}
                      {new Date(n.createdAt).toLocaleString()}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: n.body }} />
                  </div>
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
              <Avatar id={me?.id} size="sm" />
              <div style={{ flex: 1 }}>
                <RichTextEditor
                  placeholder="Add a note — paste images, drop screenshots…"
                  onSubmit={onSubmitNote}
                />
              </div>
            </div>
          </div>
        </div>
      </aside>

      {pop && <FieldPopover {...pop} onClose={() => setPop(null)} />}
      {ownerPop && (
        <OwnerPopover
          anchor={ownerPop} currentOwner={deal.owner}
          onPick={onPickOwner} onClose={() => setOwnerPop(null)}
        />
      )}
      {statusPop && (
        <StatusPopover
          anchor={statusPop} current={deal.status}
          onPick={onPickStatus} onClose={() => setStatusPop(null)}
        />
      )}
    </>
  );
}

interface StatusPopoverProps {
  anchor: DOMRect;
  current: Status;
  onPick: (next: Status) => void;
  onClose: () => void;
}

function StatusPopover({ anchor, current, onPick, onClose }: StatusPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);
  const options: Status[] = ["new", "review", "diligence"];
  return (
    <div
      ref={ref}
      style={{
        position: "fixed", top: anchor.bottom + 6, left: anchor.left,
        zIndex: 250, background: "var(--surface)", padding: 4,
        borderRadius: 8, boxShadow: "var(--shadow-pop)", width: 200,
      }}
    >
      {options.map((s) => (
        <div
          key={s} onClick={() => onPick(s)}
          style={{
            padding: "8px 12px", borderRadius: 5, cursor: "pointer",
            fontSize: 13,
            background: s === current ? "var(--bg)" : "transparent",
          }}
        >
          <Pill kind={s} />
        </div>
      ))}
    </div>
  );
}
